<template>
  <b-container class="header__account-summary ml-2 ml-md-5">
    <b-form-row v-if="isSummaryLoaded">
      <b-col 
        class="p-1 d-none d-md-block"
        align-self="end" 
      >
        <span 
          style="font-size: 2.2em;" 
          :style="{ color: isCreditLimitReached ? '#ff4757' : '#00ff61' }"
        >
          <font-awesome-icon :icon="['fas', 'dollar-sign']" />
        </span>
      </b-col>
      <b-col
        v-if="isBuyerAccount"
        v-b-tooltip.hover 
        :title="buyerBalanceTitle"
        style="flex-basis: max-content;"
        class="align-content-center"
      >
        <div 
          class="header__account-summary-balance" 
          :class="{'danger-balance': isCreditLimitReached}">
          <span v-if="hasCashBalance">{{ displayBalance }}</span>
        </div>
        <div class="header__account-summary-footer d-flex align-items-center">
          <span :class="{'text-danger': isCreditLimitReached}">{{ balanceLabel }}</span>
          
          <transition name="fade">
            <div v-if="isCreditLimitReached" class="credit-limit-badge ml-2">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
              <span>Limit Reached</span>
            </div>
            <span
              v-else-if="showMinimumBalanceUsage"
              class="balance-usage ml-1"
            >
              ({{ minimumBalanceUsagePercent }}%)
            </span>
          </transition>
        </div>
      </b-col>
      <b-col
        v-if="!isBuyerAccount"
        v-b-tooltip.hover
        title="Total amount we have paid to you"
        class="p-2"
      >
        <div class="header__account-summary-balance">
          <span v-if="hasCashBalance">{{ formattedCashBalance }}</span>
        </div>
        <div class="header__account-summary-footer">
          Received
        </div>
      </b-col>
      <b-col 
        v-if="!isBuyerAccount"
        class="p-1 ml-3 d-none d-md-block"
        align-self="end" 
      >
        <span style="font-size: 2.2em; color: #00cdff;">
          <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
        </span>
      </b-col>
      <b-col
        v-if="!isBuyerAccount" 
        v-b-tooltip.hover 
        title="Your estimated future payouts"
        class="p-2"
      >
        <div class="header__account-summary-pending">
          {{ formatCurrency(sellerPendingBalance) }}
        </div>
        <div class="header__account-summary-footer">
          Pending
        </div>
      </b-col>
    </b-form-row>

    <!-- Skeleton placeholder while loading summary -->
    <b-form-row
      v-else
      class="summary-skeleton"
      aria-label="Loading account summary"
      aria-busy="true"
    >
      <b-col
        class="p-1 d-none d-md-block"
        align-self="end"
      >
        <span style="font-size: 2.2em; color: #00ff61;">
          <font-awesome-icon :icon="['fas', 'dollar-sign']" />
        </span>
      </b-col>
      <b-col class="p-2">
        <div class="skeleton-currency-bar" />
        <div class="skeleton-label-bar mt-1" />
      </b-col>
      <b-col
        class="p-1 ml-3 d-none d-md-block"
        align-self="end"
      >
        <span style="font-size: 2.2em; color: #00cdff;">
          <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
        </span>
      </b-col>
      <b-col class="p-2">
        <div class="skeleton-currency-bar" />
        <div class="skeleton-label-bar mt-1" />
      </b-col>
    </b-form-row>
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
export default {
  name: 'AccountSummaryHeader',

  computed: {

    ...mapState('billing', [
      'accountSummary'
    ]),

    ...mapGetters('auth', [
      'isBuyerAccount',
      'accountDetails'
    ]),

    ...mapGetters('billing', [
      'sellerPendingBalance',
      'accountBillingConfiguration']),

    hasCashBalance() {
      // ensure we only show formatted currency when we have a finite numeric value
      const value = this.accountSummary?.cashBalance
      return Number.isFinite(value)
    },

    formattedCashBalance() {
      return this.hasCashBalance ? this.formatCurrency(this.accountSummary.cashBalance) : '...'
    },

    formattedMinimumBalance() {
      return this.formatCurrency(this.accountBillingConfiguration.minimumBalance)
    },

    isSummaryLoaded() {
      return this.hasCashBalance && this.accountSummary?.cashBalance !== null
    },

    showMinimumBalanceUsage() {
      return this.isBuyerAccount && this.accountDetails?.billingConfiguration?.prePay === false
    },

    minimumBalanceUsagePercent() {
      if (!this.showMinimumBalanceUsage) {
        return 0
      }

      const current = this.accountSummary.cashBalance
      if (current >= 0) {
        return 0
      }
      const usage = Math.abs(current / this.accountBillingConfiguration.minimumBalance) * 100
      return usage.toFixed(0)
    },

    displayBalance() {
      if (this.showCreditUsageMode) {
        return this.formatCurrency(Math.abs(this.accountSummary.cashBalance))
      }
      return this.formattedCashBalance
    },

    balanceLabel() {
      return this.showCreditUsageMode ? 'Credit Used' : 'Balance'
    },

    showCreditUsageMode() {
      // Show credit usage if we are on post-pay AND the balance is negative (meaning we are using credit)
      return this.showMinimumBalanceUsage && this.accountSummary.cashBalance < 0
    },

    buyerBalanceTitle() {
      if (this.isCreditLimitReached) {
        return 'Buying calls not possible anymore, need to top-up balance'
      }
      if (this.showCreditUsageMode) {
        return `Using ${this.displayBalance} of your credit limit`
      }
      return 'Your total account balance'
    },

    isCreditLimitReached() {
      return this.minimumBalanceUsagePercent >= 100
    },

    shouldPoll() {
      return true
    }
  },

  watch: {
    shouldPoll() {
      this.syncPolling()
    }
  },

  // The balance in the header is not worth a broken page: it is refreshed every 45 seconds anyway,
  // so a failed fetch is logged and the old figure is left on screen. Unhandled, these rejections
  // surfaced as an uncaught runtime error - once on load and then every time the poll came round.
  //
  // A member without the billing permission is not shown the balance and is not sent to fetch the
  // summary - that endpoint answers them with a 403. Account details are still loaded, and a buyer
  // still polls the balance endpoint: it is open to every member and its FLAGS (low balance, daily
  // budget) drive the dashboard banner and the softphone, whoever is looking.
  async created () {
    try {
      await this.fetchUsersAccountDetails()

      await this.getAccountBillingSummary()
    } catch (error) {
      console.error('Failed to load the account balance summary', error)
    }

    this.syncPolling()
  },

  destroyed () {
    this.stopPolling()
  },

  methods: {
    ...mapActions('billing', ['getAccountBillingSummary', 'getBuyerAccountBalance']),
    ...mapActions('auth', ['fetchUsersAccountDetails']),

    formatCurrency,

    /**
     * What the poll refreshes, and it is not only the figure beside this icon.
     *
     * For a BUYER it goes through `getBuyerAccountBalance`, which fetches the summary itself on its
     * way out (for a member with billing) - so the figure is refreshed either way - and additionally brings back the two flags
     * that decide whether the account may buy calls at all: `isBalanceLow` and
     * `isDailyBudgetReached`. Those drive the dashboard banner and the softphone's status, and
     * before this they were fetched by the dashboard route guard and by nothing else, which is why
     * the banner used to appear on a page refresh and on no other occasion.
     *
     * A seller has no balance endpoint of its own and no calls to buy, so it keeps the summary.
     *
     * The fallback matters more than it looks. `getBuyerAccountBalance` fetches the summary LAST,
     * so a buyer balance endpoint that fails takes the figure beside this icon down with it - the
     * poll would swallow the error and the header would sit on a stale number for the rest of the
     * session. Before the flags were folded in, the summary poll was independent and unaffected.
     */
    async refreshAccountSummary() {
      if (!this.isBuyerAccount) {
        return this.getAccountBillingSummary()
      }

      try {
        return await this.getBuyerAccountBalance()
      } catch (error) {
        console.error('Could not refresh the account balance flags', error)

        return this.getAccountBillingSummary()
      }
    },

    loadAccountSummaryInIntervals() {
      // Fetch and show account balance summary every 45 secs
      this.accountSummaryInterval = setInterval(async () => {
        try {
          await this.refreshAccountSummary()
        } catch (error) {
          console.error('Failed to refresh the account balance summary', error)
        }
      }, 45 * 1000)
    },

    stopPolling() {
      clearInterval(this.accountSummaryInterval)

      this.accountSummaryInterval = null
    },

    // Starts or stops the poll to match `shouldPoll`, and never runs two.
    syncPolling() {
      // `created` awaits two requests before it gets here: a header that went away meanwhile must
      // not start a poll nobody will stop.
      if (this._isDestroyed) {
        return
      }

      if (this.shouldPoll && !this.accountSummaryInterval) {
        this.loadAccountSummaryInIntervals()
      } else if (!this.shouldPoll && this.accountSummaryInterval) {
        this.stopPolling()
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.header__account-summary-balance {
  color: #00ff61; 
  line-height: 20px;
  font-weight: 600;
}

.header__account-summary-pending {
  color: #00cdff;
  line-height: 20px;
  font-weight: 600;
}

.header__account-summary-footer {
  color: #cfcfd0; 
  font-size: 12px;
}

.balance-usage {
  font-size: 11px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Skeleton row styles */
.summary-skeleton {
  .skeleton-currency-bar,
  .skeleton-label-bar {
    border-radius: 4px;
    background: linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.08) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.2s ease-in-out infinite;
  }
  .skeleton-currency-bar { height: 18px; width: 110px; }
  .skeleton-label-bar { height: 10px; width: 60px; }
}

// Over the balance's green, which is the same specificity and earlier.
.danger-balance {
  color: #ff4757;
  text-shadow: 0 0 10px rgba(255, 71, 87, 0.4);
}

.credit-limit-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 71, 87, 0.15);
  color: #ff4757;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  border: 1px solid rgba(255, 71, 87, 0.3);
  box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
  animation: pulse-red 2s infinite;
  white-space: nowrap;
  
  svg {
    font-size: 9px;
  }
}

@keyframes pulse-red {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.4); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(255, 71, 87, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 71, 87, 0); }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

// A phone's header has the menu button, the softphone and the user menu to fit beside this, so
// below md it keeps the amounts on one line and drops the decorative icons and the gutters. It
// holds its width: the logo is what gives way when the header runs short (see _header.scss).
@include media-breakpoint-down(sm) {
  .header__account-summary {
    flex: 0 0 auto;
    width: auto;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;

    .form-row {
      flex-wrap: nowrap;
    }
  }

  .header__account-summary-footer {
    white-space: nowrap;
  }
}
</style>
